/**
 * Main Layout Component
 * Primary app layout with header and content area
 */

import { ReactNode } from 'react';
import { Header } from './Header';
import { cn } from '@/utils/classNames';
import type { User } from '@/types';

export interface MainLayoutProps {
  children: ReactNode;
  sidebar?: ReactNode;
  className?: string;
  user?: User | null;
  onOpenSettings?: () => void;
  onOpenHelp?: () => void;
  onOpenAdmin?: () => void;
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onNavigateToAgents?: () => void;
  onNavigateToChat?: () => void;
  currentView?: 'chat' | 'agents';
}

export function MainLayout({
  children,
  sidebar,
  className,
  user,
  onOpenSettings,
  onOpenHelp,
  onOpenAdmin,
  sidebarOpen = false,
  onToggleSidebar,
  onNavigateToAgents,
  onNavigateToChat,
  currentView = 'chat',
}: MainLayoutProps) {
  return (
    <div className="flex flex-col h-screen bg-bg-secondary">
      {/* Header */}
      <Header
        user={user}
        onMenuClick={onToggleSidebar}
        onOpenSettings={onOpenSettings}
        onOpenHelp={onOpenHelp}
        onOpenAdmin={onOpenAdmin}
        onNavigateToAgents={onNavigateToAgents}
        onNavigateToChat={onNavigateToChat}
        currentView={currentView}
      />

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar (if provided) */}
        {sidebar && (
          <>
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/50 md:hidden"
                onClick={onToggleSidebar}
              />
            )}

            {/* Sidebar */}
            <aside
              className={cn(
                'fixed md:relative inset-y-0 left-0 z-50',
                'w-sidebar bg-bg-primary border-r border-border-default',
                'transition-transform duration-300',
                'md:translate-x-0',
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              )}
            >
              {sidebar}
            </aside>
          </>
        )}

        {/* Main content */}
        <main
          id="main-content"
          className={cn(
            'flex-1 flex flex-col overflow-hidden',
            className
          )}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
